import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import { RealisedWorkPhaseItemDto } from '../../types/RealisedWorkPhaseItem';
import logo from '../../assets/114.jpg';

// Define styles for the PDF
const styles = StyleSheet.create({
    page: {
        padding: 30,
        fontSize: 10,
        fontFamily: 'Helvetica',
    },
    header: {
        marginBottom: 20,
    },
    logoRow: {
        flexDirection: 'row',
        marginBottom: 15,
    },
    logoCell: {
        width: '60%',
    },
    logo: {
        width: 150,
        height: 'auto',
    },
    emptyCell: {
        width: '40%',
    },
    spacer: {
        marginBottom: 10,
    },
    headerRowTwoCol: {
        flexDirection: 'row',
        marginBottom: 5,
        fontSize: 10,
    },
    headerCellLeft: {
        width: '60%',
    },
    headerCellRight: {
        width: '40%',
    },
    headerCellBold: {
        fontWeight: 'bold',
    },
    headerCellLabel: {
        fontWeight: 'bold',
    },
    headerRowFullWidth: {
        marginBottom: 5,
        fontSize: 10,
    },
    headerCellFullWidth: {
        width: '100%',
    },
    headerRowFullWidthLabel: {
        fontWeight: 'bold',
    },
    title: {
        fontSize: 14,
        fontWeight: 'bold',
        textAlign: 'right',
    },
    groupHeader: {
        fontSize: 12,
        fontWeight: 'bold',
        backgroundColor: '#e9ecef',
        padding: 8,
        marginTop: 15,
        marginBottom: 5,
        color: '#1c7ed6',
    },
    table: {
        marginTop: 10,
    },
    tableHeader: {
        flexDirection: 'row',
        backgroundColor: '#dee2e6',
        borderBottomWidth: 2,
        borderBottomColor: '#495057',
        paddingVertical: 8,
        paddingHorizontal: 4,
        fontWeight: 'bold',
        fontSize: 8,
    },
    tableRow: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderBottomColor: '#dee2e6',
        paddingVertical: 6,
        paddingHorizontal: 4,
        fontSize: 8,
    },
    tableRowAlt: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderBottomColor: '#dee2e6',
        paddingVertical: 6,
        paddingHorizontal: 4,
        backgroundColor: '#f8f9fa',
        fontSize: 8,
    },
    col1: { width: '32%' }, // Work Item Name
    col2: { width: '10%', textAlign: 'center' }, // Unit (YKS.)
    col3: { width: '12%', textAlign: 'right' }, // Amount (MÄÄRÄ)
    col4: { width: '12%', textAlign: 'right' }, // m² (area)
    col5: { width: '12%', textAlign: 'right' }, // kg/m² (calculated)
    col6: { width: '12%', textAlign: 'right' }, // Unit Price
    col7: { width: '10%', textAlign: 'right' }, // Total Price
    totalSection: {
        marginTop: 20,
        paddingTop: 10,
        borderTopWidth: 2,
        borderTopColor: '#495057',
    },
    totalRow: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        marginBottom: 8,
        paddingRight: 4,
    },
    totalLabel: {
        fontWeight: 'bold',
        marginRight: 20,
        fontSize: 11,
    },
    totalValue: {
        fontWeight: 'bold',
        width: 100,
        textAlign: 'right',
        fontSize: 11,
    },
    signatureSection: {
        position: 'absolute',
        bottom: 80,
        left: 30,
        right: 30,
    },
    signatureRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 10,
    },
    signatureColumn: {
        width: '45%',
    },
    signatureLine: {
        borderBottomWidth: 1,
        borderBottomColor: '#000',
        marginBottom: 5,
        height: 40,
    },
    signatureLabel: {
        fontSize: 9,
        marginBottom: 3,
    },
    signatureName: {
        fontSize: 9,
    },
    footer: {
        position: 'absolute',
        bottom: 30,
        left: 30,
        fontSize: 8,
        color: '#000',
    },
    changePositive: {
        color: '#2f9e44',
    },
    changeNegative: {
        color: '#c92a2a',
    },
});

interface MeasurementReportPDFProps {
    items: RealisedWorkPhaseItemDto[];
    totalCost: number;
    totalChange: number;
    form: any;
    objectId?: number;
    contractId?: number;
    contract?: any;
    getTranslation?: (key: string) => string | null;
}

export const MeasurementReportPDF = ({
    items,
    totalCost,
    totalChange,
    form,
    objectId,
    contract,
    getTranslation = (key: string) => key,
}: MeasurementReportPDFProps) => {

    // Extract values from contract data (primary source) or form (fallback)
    const contractCode = contract?.contractCode || form?.values?.contractCode || '';
    const contractorName = contract?.contractorName || form?.values?.contractorName || '';
    const contractCity = contract?.contractCity || '';
    const location = form?.values?.location || form?.values?.name || '';
    const measurementDate = form?.values?.measurementDate;
    const measurementPersons = form?.values?.measurementPersons || '';
    const readyDate = form?.values?.readyDate || form?.values?.completedPvm;
    const orderNumber = form?.values?.orderNumber || form?.values?.orderName || '';
    const contractorWorkNumber = form?.values?.contractorWorkNumber || form?.values?.contractorWorkNumber || '';
    const streetName = form?.values?.streetName;
    const foremanName = form?.values?.foreman?.name || form?.values?.foremanName || '';
    const contractorForemanName = form?.values?.contractorForeman?.name || form?.values?.contractorForemanName || '';
    const typeName = form?.values?.typeDesc || '';
    
    // Translation labels - matching PHP: getPageText("TEXT_LABEL_NIMI1", true), etc.
    const orgLabel1 = getTranslation("LABEL_MEASUREMENT_REPORT_ORG_LABEL1");
    const orgLabel2 = getTranslation("LABEL_MEASUREMENT_REPORT_ORG_LABEL2");
    const orgLabel3 = (typeName || '').toUpperCase();
    
    // Format date helper
    const formatDate = (date: any): string => {
        if (!date) return '';
        try {
            const d = new Date(date);
            return d.toLocaleDateString('fi-FI');
        } catch {
            return '';
        }
    };
    
    // Group items by their group headers
    const groupedItems: { [key: string]: RealisedWorkPhaseItemDto[] } = {};
    let currentGroup = '';

    items.forEach((item) => {
        if (item.isGroupHeader) {
            currentGroup = item.name;
            groupedItems[currentGroup] = [];
        } else if (currentGroup) {
            groupedItems[currentGroup].push(item);
        }
    });

    const formatNumber = (value: number | null | undefined): string => {
        if (value === null || value === undefined || value === 0) return '-';
        return value.toFixed(2);
    };

    // Calculate kg/m² based on the unit and available data
    const calculateUsage = (item: RealisedWorkPhaseItemDto): string => {
        const amount = item.realisedAmount || 0;
        const area = item.realisedArea || 0;
        const weight = item.realisedWeight || 0;
        const unit = item.unitName?.toLowerCase() || '';

        if (area === 0) return '-';

        let usage = 0;
        if (unit === 't' || unit === 'tn') {
            // If unit is tonnes, calculate: 1000 * tonnes / m²
            usage = (amount / area) * 1000;
        } else if (unit === 'm²' || unit === 'm2') {
            // If unit is m², calculate: 1000 * weight / amount
            if (amount !== 0) {
                usage = (weight / amount) * 1000;
            }
        }

        return usage > 0 ? usage.toFixed(2) : '-';
    };

    return (
        <Document>
            <Page size="A4" orientation="portrait" style={styles.page}>
                {/* Header Section - Matching Legacy Format */}
                <View style={styles.header}>
                    {/* Logo Row */}
                    <View style={styles.logoRow}>
                        <View style={styles.logoCell}>
                            <Image src={logo} style={styles.logo} />
                        </View>
                        <View style={styles.emptyCell} />
                    </View>
                    
                    <View style={styles.spacer} />
                    
                    {/* Organization Name and Title */}
                    <View style={styles.headerRowTwoCol}>
                        <Text style={[styles.headerCellLeft, styles.headerCellBold]}>{orgLabel1}</Text>
                        <Text style={[styles.headerCellRight, styles.title, { textAlign: 'left' }]}>
                            {getTranslation("LABEL_MEASUREMENT_REPORT_TITLE")}
                        </Text> {/* MITTAPÖYTÄKIRJA */}
                    </View>
                    <View style={styles.headerRowTwoCol}>
                        <Text style={styles.headerCellLeft}>{orgLabel2}</Text>
                        <Text style={styles.headerCellRight}>
                            {contractCode || '-'}
                        </Text>
                    </View>
                    <View style={styles.headerRowTwoCol}>
                        <Text style={[styles.headerCellLeft, styles.headerCellBold]}>{orgLabel3}</Text>
                        <Text style={styles.headerCellRight}>
                            {contractCity} {new Date().toLocaleDateString('fi-FI')}
                        </Text>
                    </View>
                    
                    {/* Subscriber - conditional full-width row */}
                    {(() => {
                        const subscriber = form?.values?.subscriber;
                        if (!subscriber) return null;
                        
                        const subscriberDisplay = [
                            subscriber.name,
                            subscriber.organization1,
                            subscriber.organization2
                        ].filter(Boolean).join(' ').trim();
                        
                        if (!subscriberDisplay) return null;
                        
                        return (
                            <View style={styles.headerRowFullWidth}>
                                <Text style={[styles.headerCellFullWidth, styles.headerCellBold]}>
                                    {subscriberDisplay}
                                </Text>
                            </View>
                        );
                    })()}
                    
                    <View style={styles.spacer} />
                    
                    {/* Contractor and Framework Agreement */}
                    <View style={styles.headerRowTwoCol}>
                        <Text style={styles.headerCellLeft}>
                            <Text style={styles.headerCellLabel}>Contractor Name:</Text> {contractorName || '-'}
                        </Text>
                        <Text style={[styles.headerCellRight, styles.headerCellBold]}>
                            {getTranslation("LABEL_FRAMEWORK_AGREEMENT")}
                            {/* <Text style={styles.headerCellLabel}>Contract Framework Number:</Text> {contractFrameworkNumber || '-'} */}
                        </Text>
                    </View>
                    <View style={styles.headerRowTwoCol}>
                        <Text style={styles.headerCellLeft}>
                            <Text style={styles.headerCellLabel}>{getTranslation("LABEL_MEASUREMENT_REPORT_LOCATION") || "Location:"}</Text> {location || '-'}
                        </Text>  {/* TYÖKOHDE */}
                        {/* <Text style={styles.headerCellRight}>
                            <Text style={styles.headerCellLabel}>Contract Number:</Text> {contractNumber || '-'}
                        </Text> */}
                    </View>
                    
                    <View style={styles.spacer} />
                    
                    {/* Measurement Date */}
                    <View style={styles.headerRowTwoCol}>
                        <Text style={styles.headerCellLeft}>
                            <Text style={styles.headerCellLabel}>{getTranslation("LABEL_MEASUREMENT_REPORT_DATE") || "Measurement Date:"}</Text> {formatDate(measurementDate) || '-'}
                        </Text> {/* MITTAUS PVM */}
                        <Text style={styles.headerCellRight}></Text>
                    </View>
                    
                    {/* Measurement Persons */}
                    <View style={styles.headerRowFullWidth}>
                        <Text>
                            <Text style={styles.headerRowFullWidthLabel}>{getTranslation("LABEL_MEASUREMENT_REPORT_MEASURED_BY") || "Measured by:"}</Text> {measurementPersons || '-'}
                        </Text> {/* MITTAUKSEN SUORITTAJAT */}
                    </View>
                    
                    {/* Work Completed and Order Number */}
                    <View style={styles.headerRowTwoCol}>
                        <Text style={styles.headerCellLeft}>
                            <Text style={styles.headerCellLabel}>{getTranslation("LABEL_MEASUREMENT_REPORT_READY_DATE") || "Ready Date:"}</Text> {formatDate(readyDate) || '-'}
                        </Text> {/* TYÖ VALMIS */}
                        <View style={styles.headerCellRight}>
                            <Text style={styles.headerCellBold}>{getTranslation("LABEL_MEASUREMENT_REPORT_ORDER_NUMBER")}</Text>
                            <Text>{orderNumber || '-'}</Text>
                        </View> {/* OSTOTILAUS */}
                    </View>
                    
                    {/* Contractor Work Number */}
                    <View style={styles.headerRowFullWidth}>
                        <Text>
                            <Text style={styles.headerRowFullWidthLabel}>{getTranslation("LABEL_MEASUREMENT_REPORT_CONTRACTOR_WORK_NUMBER")}</Text> {contractorWorkNumber || '-'}
                        </Text> {/* URAKOITSIJAN TYÖNUMERO */}
                    </View>
                    
                    {/* Network and Phase - commented out to remove extra spacing
                    <View style={styles.headerRowTwoCol}>
                        <Text style={styles.headerCellLeft}>
                            <Text style={styles.headerCellLabel}>Network Number:</Text> {networkNumber || '-'}
                        </Text>
                        <Text style={styles.headerCellRight}></Text>
                    </View>
                    */}
                    
                    {/* Street Name (conditional) */}
                    {streetName?.trim() && (
                        <View style={styles.headerRowTwoCol}>
                            <Text style={styles.headerCellLeft}>
                                <Text style={styles.headerCellLabel}>{getTranslation("LABEL_MEASUREMENT_REPORT_STREET_NAME") || "Street Name:"}</Text> {streetName}
                            </Text> {/* KADUNNNIMI */}
                            <Text style={styles.headerCellRight}></Text>
                        </View>
                    )}
                    
                    {/* Foreman */}
                    <View style={styles.headerRowFullWidth}>
                        <Text>
                            <Text style={styles.headerRowFullWidthLabel}>{getTranslation("LABEL_MEASUREMENT_REPORT_FOREMAN") || "Foreman:"}</Text> {foremanName || '-'}
                        </Text> {/* TYÖNJOHTAJA */}
                    </View>
                </View>

                {/* Work Items by Group */}
                {Object.entries(groupedItems).map(([groupName, groupItems]) => (
                    <View key={groupName} wrap={false}>
                        <Text style={styles.groupHeader}>{groupName}</Text>
                        
                        {/* Table Header */}
                        <View style={styles.tableHeader}>
                            <Text style={styles.col1}>{getTranslation("LABEL_MEASUREMENT_REPORT_WORK_ITEM") || "Work Item"}</Text>
                            <Text style={styles.col2}>{getTranslation("LABEL_MEASUREMENT_REPORT_UNIT") || "Unit"}</Text>
                            <Text style={styles.col3}>{getTranslation("LABEL_MEASUREMENT_REPORT_AMOUNT") || "Amount"}</Text>
                            <Text style={styles.col4}>{getTranslation("LABEL_MEASUREMENT_REPORT_AREA") || "m²"}</Text>
                            <Text style={styles.col5}>{getTranslation("LABEL_MEASUREMENT_REPORT_USAGE") || "kg/m²"}</Text>
                            <Text style={styles.col6}>{getTranslation("LABEL_MEASUREMENT_REPORT_UNIT_PRICE") || "Unit Price"}</Text>
                            <Text style={styles.col7}>{getTranslation("LABEL_MEASUREMENT_REPORT_TOTAL") || "Total"}</Text>
                        </View>

                        {/* Table Rows */}
                        {groupItems.map((item, idx) => (
                            <View key={`${item.workItemId}-${item.priceItemId}-${idx}`} style={idx % 2 === 0 ? styles.tableRow : styles.tableRowAlt}>
                                <Text style={styles.col1}>{item.name}</Text>
                                <Text style={styles.col2}>{item.unitName}</Text>
                                <Text style={styles.col3}>{formatNumber(item.realisedAmount)}</Text>
                                <Text style={styles.col4}>{formatNumber(item.realisedArea)}</Text>
                                <Text style={styles.col5}>{calculateUsage(item)}</Text>
                                <Text style={styles.col6}>{formatNumber(item.unitPrice)}</Text>
                                <Text style={styles.col7}>{formatNumber(item.price)}</Text>
                            </View>
                        ))}
                    </View>
                ))}

                {/* Totals Section */}
                <View style={styles.totalSection}>
                    <View style={styles.totalRow}>
                        <Text style={styles.totalLabel}>{getTranslation("LABEL_MEASUREMENT_REPORT_TOTAL_LABEL") || "Total:"}</Text>
                        <Text style={styles.totalValue}>{totalCost.toFixed(2)} €</Text>
                    </View>
                    {/* <View style={styles.totalRow}>
                        <Text style={styles.totalLabel}>Retention (5%):</Text>
                        <Text style={styles.totalValue}>{(totalCost * 0.05).toFixed(2)} €</Text>
                    </View>
                    <View style={styles.totalRow}>
                        <Text style={styles.totalLabel}>Total Net:</Text>
                        <Text style={[styles.totalValue, { color: '#2b8a3e' }]}>{(totalCost - totalCost * 0.05).toFixed(2)} €</Text>
                    </View> */}
                </View>

                {/* Signature Section */}
                <View style={styles.signatureSection}>
                    <View style={styles.signatureRow}>
                        <View style={styles.signatureColumn}>
                            <View style={styles.signatureLine} />
                            <Text style={[styles.signatureLabel, { fontWeight: 'bold' }]}>
                                {getTranslation("LABEL_MEASUREMENT_REPORT_SIGNATURE_1")}
                            </Text>
                            <Text style={styles.signatureName}>{foremanName || '-'}</Text>
                        </View>
                        <View style={styles.signatureColumn}>
                            <View style={styles.signatureLine} />
                            <Text style={[styles.signatureLabel, { fontWeight: 'bold' }]}>
                                {getTranslation("LABEL_MEASUREMENT_REPORT_SIGNATURE_2")}
                            </Text>
                            <Text style={styles.signatureName}>{contractorForemanName || '-'}</Text>
                        </View>
                    </View>
                </View>

                {/* Footer */}
                <Text style={styles.footer}>
                    <Text style={{ fontWeight: 'bold' }}>{getTranslation("LABEL_MEASUREMENT_REPORT_ID") || "ID:"}</Text><Text> {objectId || '-'}</Text>
                </Text>
            </Page>
        </Document>
    );
};
